import { useState, useMemo, useRef, useEffect, useCallback } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { authFetch } from '../api/authFetch';
import { useFileDrawerStore } from '../state/fileDrawerStore';
import { useAllSessions } from '../state/sessionListStore';

/* ── Helpers (mirrored from UnifiedDrawer) ─────────────────────────── */

function modelBadge(model: string | null, isRelay: boolean): { label: string; cls: string } {
  if (isRelay) return { label: 'Relay', cls: 'relay' };
  if (!model) return { label: 'Sonnet', cls: 'cascade' };
  if (model.includes('opus')) return { label: 'Opus', cls: 'opus' };
  if (model.includes('sonnet')) return { label: 'Sonnet', cls: 'sonnet' };
  if (model.includes('haiku')) return { label: 'Haiku', cls: 'haiku' };
  if (model.includes('gemini-3')) return { label: 'Gemini 3', cls: 'gemini' };
  if (model.includes('gemini') && model.includes('flash-lite')) return { label: 'Flash Lite', cls: 'gemini' };
  if (model.includes('gemini') && model.includes('flash')) return { label: 'Flash', cls: 'gemini' };
  if (model.includes('gemini')) return { label: 'Gemini', cls: 'gemini' };
  const parts = model.split('-');
  return { label: parts[parts.length - 1] ?? 'AI', cls: '' };
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/* ── SharePayload type ────────────────────────────────────────────── */

export interface SharePayload {
  type: 'text' | 'file' | 'messages';
  /** Raw text content — for text/messages types */
  text?: string;
  /** File path (project-relative, e.g. voice-assistant/plans/foo.md) — for file type */
  filePath?: string;
  /** Display name shown in modal header */
  fileName?: string;
  /** Title for PDF header */
  title?: string;
  /** Show PDF download option in external share (default: true for text, auto-detected for files) */
  pdfCapable?: boolean;
  /** Show image/video embed toggles (for .md files with inline media) */
  mediaEmbeddable?: boolean;
  /** Source session ID — embedded in saved-post YAML frontmatter for "↩ Source" navigation */
  sourceSessionId?: string;
  /** Source message IDs — embedded in saved-post YAML frontmatter */
  sourceMessageIds?: string[];
}

/* ── Props ─────────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  currentSessionId: string | null;
  /** Called when user picks an existing session as target */
  onSendToSession: (sessionId: string) => void;
  /** Called when user wants to create a new session as target */
  onNewSession: () => void;
  /** Called when user wants to share externally via native share / clipboard */
  onShareExternal: () => void;
  /** Called when user wants to download/share as PDF (async — modal stays open while generating) */
  onDownloadPdf?: () => Promise<void>;
  /** Called to share file as PDF with options (filePath, includeImages, attachVideos, videoPaths) */
  onDownloadFilePdf?: (opts: { filePath: string; includeImages: boolean; attachVideos: boolean; videoPaths: string[] }) => Promise<void>;
  /** Called to share file as raw text externally */
  onShareFileExternal?: (filePath: string, fileName: string) => Promise<void>;
  onClose: () => void;
  /** When false, show upsell instead of session list */
  interAgentSharingEnabled?: boolean;
  /** Share payload — controls header, sections, and available options */
  payload?: SharePayload | null;
}

/* ── Media parser (moved from FileListDrawer) ─────────────────────── */

function parseMediaFromMarkdown(content: string): { imageCount: number; videoPaths: string[] } {
  const imageRegex = /!\[[^\]]*\]\(([^)]+)\)/g;
  const videoRegex = /\[[^\]]*\]\(([^)]+\.(?:mp4|webm|mov|avi|mkv)(?:[^)]*)?)\)/gi;
  let imageCount = 0;
  const videoPaths: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = imageRegex.exec(content)) !== null) imageCount++;
  while ((m = videoRegex.exec(content)) !== null) videoPaths.push(m[1]);
  return { imageCount, videoPaths };
}

/* ── Component ─────────────────────────────────────────────────────── */

export function ShareTargetModal({
  open,
  currentSessionId,
  onSendToSession,
  onNewSession,
  onShareExternal,
  onDownloadPdf,
  onDownloadFilePdf,
  onShareFileExternal,
  onClose,
  interAgentSharingEnabled = true,
  payload,
}: Props) {
  const sessions = useAllSessions();
  const [query, setQuery] = useState('');
  const [pdfState, setPdfState] = useState<'idle' | 'generating' | 'error'>('idle');
  const pdfErrTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Save as Post section state ─────────────────────────────────── */
  const [savePostExpanded, setSavePostExpanded] = useState(false);
  const [postName, setPostName] = useState('');
  const [postSaveState, setPostSaveState] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const postErrTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileDrawerRefresh = useFileDrawerStore((s) => s.refresh);

  /* ── External share section state (for file payloads) ─────────── */
  const [externalExpanded, setExternalExpanded] = useState(false);
  const [shareFormat, setShareFormat] = useState<'pdf' | 'text'>('pdf');
  const [embedImages, setEmbedImages] = useState(true);
  const [attachVideos, setAttachVideos] = useState(false);
  const [mediaInfo, setMediaInfo] = useState<{ imageCount: number; videoPaths: string[] } | null>(null);
  const [mediaLoading, setMediaLoading] = useState(false);

  const isFilePayload = payload?.type === 'file' && payload.filePath;
  const showPdfOption = isFilePayload ? (payload?.pdfCapable ?? false) : !!onDownloadPdf;

  /* Load media info when file payload is markdown */
  useEffect(() => {
    if (!open || !isFilePayload || !payload?.mediaEmbeddable) {
      setMediaInfo(null);
      return;
    }
    setMediaLoading(true);
    (async () => {
      try {
        const { authFetch } = await import('../api/authFetch');
        const res = await authFetch(`/api/files/read?path=${encodeURIComponent(payload.filePath!)}&full=1`);
        if (res.ok) {
          const data = await res.json();
          const content = (data.lines as string[]).join('\n');
          setMediaInfo(parseMediaFromMarkdown(content));
        }
      } catch { /* ignore */ }
      setMediaLoading(false);
    })();
  }, [open, isFilePayload, payload?.filePath, payload?.mediaEmbeddable]);

  /* Reset state when modal opens/closes */
  useEffect(() => {
    if (open) {
      setQuery('');
      setPdfState('idle');
      setExternalExpanded(false);
      setSavePostExpanded(false);
      setPostSaveState('idle');
      // Default to 'text' (raw download) for non-PDF-capable files
      setShareFormat(showPdfOption ? 'pdf' : 'text');
      setEmbedImages(true);
      setAttachVideos(false);
      // Pre-fill post name from title or first 40 chars of text
      const raw = payload?.title || payload?.text?.split('\n').find(l => l.trim()) || 'post';
      const slug = raw.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
      const datePart = new Date().toISOString().slice(0, 10);
      setPostName(`${datePart}-${slug}`);
    }
  }, [open, showPdfOption]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Save selected messages as a markdown post in _posts/ */
  const handleSavePost = useCallback(async () => {
    if (postSaveState === 'saving' || !payload?.text) return;
    const slug = postName.trim().replace(/\.md$/, '');
    if (!slug) return;

    const fileName = `${slug}.md`;
    const frontmatter = [
      '---',
      `title: "${(payload.title || slug).replace(/"/g, "'")}"`,
      `savedAt: "${new Date().toISOString()}"`,
      ...(payload.sourceSessionId ? [`sourceSession: "${payload.sourceSessionId}"`] : []),
      ...(payload.sourceMessageIds?.length ? [`sourceMessages: [${payload.sourceMessageIds.map(id => `"${id}"`).join(', ')}]`] : []),
      '---',
      '',
    ].join('\n');

    setPostSaveState('saving');
    try {
      const res = await authFetch('/api/files/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: `_posts/${fileName}`, content: frontmatter + payload.text }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `HTTP ${res.status}`);
      }
      setPostSaveState('done');
      fileDrawerRefresh();
      postErrTimer.current = setTimeout(() => setPostSaveState('idle'), 3000);
    } catch {
      setPostSaveState('error');
      postErrTimer.current = setTimeout(() => setPostSaveState('idle'), 3000);
    }
  }, [postSaveState, payload, postName, fileDrawerRefresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sessions
      .filter(s => {
        const name = (s.name || s.previewText || '').toLowerCase();
        return !q || name.includes(q);
      })
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [sessions, query]);

  if (!open) return null;

  /* ── Dynamic header ─────────────────────────────────────────────── */
  const headerTitle = isFilePayload
    ? `Share: ${payload.fileName || payload.filePath!.split('/').pop() || 'file'}`
    : payload?.type === 'messages'
      ? 'Share messages'
      : 'Send to...';

  /* ── PDF handler — dispatches to the right backend route ────────── */
  const handlePdf = async () => {
    if (pdfState === 'generating') return;
    setPdfState('generating');
    if (pdfErrTimer.current) clearTimeout(pdfErrTimer.current);
    try {
      if (isFilePayload && onDownloadFilePdf) {
        await onDownloadFilePdf({
          filePath: payload.filePath!,
          includeImages: embedImages,
          attachVideos,
          videoPaths: mediaInfo?.videoPaths ?? [],
        });
      } else if (onDownloadPdf) {
        await onDownloadPdf();
      }
      setPdfState('idle');
      onClose();
    } catch {
      setPdfState('error');
      pdfErrTimer.current = setTimeout(() => setPdfState('idle'), 3000);
    }
  };

  /* ── External share handler ─────────────────────────────────────── */
  const handleExternalShare = async () => {
    if (isFilePayload && shareFormat === 'pdf' && onDownloadFilePdf) {
      await handlePdf();
    } else if (isFilePayload && shareFormat === 'text' && onShareFileExternal) {
      try {
        await onShareFileExternal(payload.filePath!, payload.fileName || payload.filePath!.split('/').pop() || 'file');
        onClose();
      } catch { /* ignore */ }
    } else {
      onShareExternal();
      onClose();
    }
  };

  const hasMediaOptions = isFilePayload && payload.mediaEmbeddable && mediaInfo && (mediaInfo.imageCount > 0 || mediaInfo.videoPaths.length > 0);

  return createPortal(
    <div class="share-target-backdrop" onClick={onClose}>
      <div class="share-target-sheet" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div class="share-target-header">
          <span class="share-target-title">{headerTitle}</span>
          <button class="share-target-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Section 1: Share to Agent ─────────────────────────── */}
        <div class="share-target-section-label">Share to Agent</div>

        {/* Search */}
        <div class="share-target-search-row">
          <input
            class="share-target-search"
            type="search"
            placeholder="Search sessions…"
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            autoFocus
          />
        </div>

        {/* Session list or upsell */}
        {!interAgentSharingEnabled ? (
          <div class="share-target-upsell">
            <div class="share-target-upsell-icon">🔗</div>
            <div class="share-target-upsell-title">Inter-agent sharing</div>
            <div class="share-target-upsell-body">
              Send context between sessions — available on Premium. Use external share below to copy or send via your apps.
            </div>
          </div>
        ) : (
          <div class="share-target-list">
            {/* New session row — always at top */}
            <button
              class="share-target-row new-session-row"
              onClick={() => { onNewSession(); onClose(); }}
            >
              <span class="share-target-row-icon">✨</span>
              <div class="share-target-row-body">
                <span class="share-target-row-name">New session</span>
                <span class="share-target-row-meta">Start a fresh conversation with this context</span>
              </div>
            </button>

            {/* Existing sessions */}
            {filtered.map((s) => {
              const isCurrent = s.id === currentSessionId;
              const mb = modelBadge(s.model, s.isRelay);
              const displayName = s.name || s.previewText || 'Untitled session';
              const truncated = displayName.length > 55 ? displayName.slice(0, 55) + '…' : displayName;
              return (
                <button
                  key={s.id}
                  class={`share-target-row${isCurrent ? ' current' : ''}`}
                  onClick={() => { onSendToSession(s.id); onClose(); }}
                >
                  <span class="share-target-row-icon">
                    {isCurrent ? '▶' : '💬'}
                  </span>
                  <div class="share-target-row-body">
                    <span class="share-target-row-name">{truncated}</span>
                    <div class="share-target-row-meta">
                      <span class={`session-model-badge ${mb.cls}`}>{mb.label}</span>
                      <span class={`status-badge ${s.status}`}>{s.status}</span>
                      <span>{timeAgo(s.updatedAt)}</span>
                      {isCurrent && <span class="share-target-current-label">current</span>}
                    </div>
                  </div>
                </button>
              );
            })}

            {filtered.length === 0 && query && (
              <div class="share-target-empty">No sessions match "{query}"</div>
            )}
          </div>
        )}

        {/* ── Section 2: Share Externally ──────────────────────── */}
        <div class="share-target-section-label share-target-section-external"
          onClick={() => setExternalExpanded(!externalExpanded)}
        >
          <span>Share Externally</span>
          <span class="share-target-section-chevron">{externalExpanded ? '▾' : '▸'}</span>
        </div>

        {externalExpanded && (
          <div class="share-target-external-section">
            {/* For file payloads with PDF capability: format picker */}
            {isFilePayload && showPdfOption && (
              <div class="share-target-format-picker">
                <label class="share-target-format-radio">
                  <input type="radio" name="share-fmt" checked={shareFormat === 'pdf'} onChange={() => setShareFormat('pdf')} />
                  <span>PDF document</span>
                </label>
                <label class="share-target-format-radio">
                  <input type="radio" name="share-fmt" checked={shareFormat === 'text'} onChange={() => setShareFormat('text')} />
                  <span>Raw text / file</span>
                </label>
              </div>
            )}

            {/* Media options (embed images, attach videos) */}
            {isFilePayload && shareFormat === 'pdf' && hasMediaOptions && (
              <div class="share-target-media-options">
                {mediaInfo!.imageCount > 0 && (
                  <label class="share-target-media-toggle">
                    <input type="checkbox" checked={embedImages} onChange={(e: Event) => setEmbedImages((e.target as HTMLInputElement).checked)} />
                    <span>Embed {mediaInfo!.imageCount} image{mediaInfo!.imageCount !== 1 ? 's' : ''}</span>
                  </label>
                )}
                {mediaInfo!.videoPaths.length > 0 && (
                  <label class="share-target-media-toggle">
                    <input type="checkbox" checked={attachVideos} onChange={(e: Event) => setAttachVideos((e.target as HTMLInputElement).checked)} />
                    <span>Attach {mediaInfo!.videoPaths.length} video{mediaInfo!.videoPaths.length !== 1 ? 's' : ''}</span>
                  </label>
                )}
              </div>
            )}

            {isFilePayload && shareFormat === 'pdf' && mediaLoading && (
              <div class="share-target-media-loading">Checking media…</div>
            )}

            {/* Action buttons */}
            <div class="share-target-external-actions">
              {/* For non-file payloads: simple text share + PDF */}
              {!isFilePayload && (
                <>
                  <button class="share-target-external-btn" onClick={() => { onShareExternal(); onClose(); }}>
                    <span>Share as text</span>
                    <span class="share-target-external-icon">↗</span>
                  </button>
                  {onDownloadPdf && (
                    <button
                      class={`share-target-pdf-btn${pdfState === 'generating' ? ' generating' : ''}${pdfState === 'error' ? ' error' : ''}`}
                      disabled={pdfState === 'generating'}
                      onClick={handlePdf}
                    >
                      {pdfState === 'generating' ? (
                        <><span class="share-target-pdf-spinner" />Generating…</>
                      ) : pdfState === 'error' ? (
                        <>⚠ PDF failed</>
                      ) : (
                        <><span class="share-target-pdf-icon">⬇</span>Download PDF</>
                      )}
                    </button>
                  )}
                </>
              )}

              {/* For file payloads: unified share button */}
              {isFilePayload && (
                <button
                  class={`share-target-external-btn share-target-external-btn--action${pdfState === 'generating' ? ' generating' : ''}`}
                  disabled={pdfState === 'generating'}
                  onClick={handleExternalShare}
                >
                  {pdfState === 'generating' ? (
                    <><span class="share-target-pdf-spinner" />Generating…</>
                  ) : pdfState === 'error' ? (
                    <>⚠ Failed — tap to retry</>
                  ) : shareFormat === 'pdf' ? (
                    <><span class="share-target-pdf-icon">⬇</span>Share as PDF</>
                  ) : showPdfOption ? (
                    <>Share as text ↗</>
                  ) : (
                    <><span class="share-target-pdf-icon">⬇</span>Download file</>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Section 3: Save as Post (text/messages only) ────────── */}
        {!isFilePayload && payload?.text && (
          <>
            <div class="share-target-section-label share-target-section-external"
              onClick={() => setSavePostExpanded(!savePostExpanded)}
            >
              <span>Save as Post</span>
              <span class="share-target-section-chevron">{savePostExpanded ? '▾' : '▸'}</span>
            </div>
            {savePostExpanded && (
              <div class="share-target-external-section">
                <div class="share-post-name-row">
                  <input
                    class="share-post-name-input"
                    type="text"
                    value={postName}
                    onInput={(e) => setPostName((e.target as HTMLInputElement).value)}
                    placeholder="filename-slug (no .md needed)"
                    maxLength={80}
                  />
                </div>
                <div class="share-target-external-actions">
                  <button
                    class={`share-target-external-btn${postSaveState === 'saving' ? ' generating' : ''}${postSaveState === 'error' ? ' error' : ''}${postSaveState === 'done' ? ' done' : ''}`}
                    disabled={postSaveState === 'saving' || !postName.trim()}
                    onClick={handleSavePost}
                  >
                    {postSaveState === 'saving' ? (
                      <><span class="share-target-pdf-spinner" />Saving…</>
                    ) : postSaveState === 'done' ? (
                      <>✓ Saved to _posts/</>
                    ) : postSaveState === 'error' ? (
                      <>⚠ Save failed — tap to retry</>
                    ) : (
                      <><span class="share-target-pdf-icon">💾</span>Save as Post</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
